import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { HintText } from '@/lib/base/forms/hint-text';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { PropertyValueLabel } from '../properties/labels/PropertyValueLabel';
import { PropertyInput } from '../properties/PropertyInput';
import { LabelWithIndicator } from './LabelWithIndicator';

export interface CrmPropertyFieldProps {
  /** Property type schema with label, field type, etc. */
  propertyType: PropertyTypeSchema;
  /**
   * Override the schema label. Pass `''` to render no label row at all —
   * for a field whose section heading already names it.
   */
  label?: string;
  /** Override the schema's required flag for the indicator. */
  isRequired?: boolean;
  /** Help text shown below the input while no error is displayed. */
  hint?: ReactNode;
  /** Current property value */
  value: string | number | object | null | undefined;
  /** Callback when value changes */
  onChange: (value: unknown) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** When true, renders value as text instead of an input */
  readOnly?: boolean;
  /** Whether the field has been edited (shows blue dot) */
  isEdited?: boolean;
  /** Error message to display */
  error?: string;
  /** Input size */
  size?: 'sm' | 'md';
  /** Optional element to render on the right side of the label (e.g., EnrichIcon) */
  labelRightElement?: ReactNode;
  /** Trailing addon element inside the input (only supported for phone_number field type) */
  trailingAddon?: ReactNode;
  /** Placeholder text to show in read-only mode when value is empty */
  emptyPlaceholder?: string;
  /** Custom read-only content to replace the default PropertyValueLabel */
  customReadOnlyContent?: React.ReactNode;
}

/**
 * CrmPropertyField - Wrapper component that renders a label + PropertyInput + error.
 *
 * This component composes:
 * - LabelWithIndicator: Shows label, required asterisk, tooltip, and edit indicator
 * - PropertyInput: The actual input component based on field type (edit mode)
 * - PropertyValueLabel: Read-only text display of the value (read-only mode)
 * - Error display: Shows error message below the input
 *
 * Use this for form contexts where labels are needed.
 * Use PropertyInput directly for filter contexts where labels aren't needed.
 *
 * @example
 * // Edit mode (default)
 * <CrmPropertyField
 *   propertyType={emailSchema}
 *   value={email}
 *   onChange={setEmail}
 *   isEdited={hasChanges}
 *   error={validationError}
 * />
 *
 * @example
 * // Read-only mode
 * <CrmPropertyField
 *   propertyType={emailSchema}
 *   value={email}
 *   onChange={setEmail}
 *   readOnly
 * />
 */
export function CrmPropertyField({
  propertyType,
  label,
  isRequired,
  hint,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  isEdited = false,
  error,
  // size prop will be passed to PropertyInput in Phase 3
  size: _size = 'sm',
  labelRightElement,
  trailingAddon,
  emptyPlaceholder,
  customReadOnlyContent,
}: CrmPropertyFieldProps) {
  const isValueEmpty = value === null || value === undefined || value === '';
  const resolvedLabel = label ?? propertyType.label;
  const resolvedRequired = isRequired ?? propertyType.is_required;

  const readOnlyContent = useMemo(() => {
    if (
      customReadOnlyContent !== null &&
      customReadOnlyContent !== undefined &&
      customReadOnlyContent !== false
    ) {
      return customReadOnlyContent;
    }
    if (isValueEmpty && emptyPlaceholder) {
      return <span className='text-sm text-tertiary'>{emptyPlaceholder}</span>;
    }
    return (
      <PropertyValueLabel
        value={value}
        propertyType={propertyType}
        fontSize='sm'
        maxLength={150}
      />
    );
  }, [
    customReadOnlyContent,
    isValueEmpty,
    emptyPlaceholder,
    value,
    propertyType,
  ]);

  return (
    <div className='flex flex-col gap-1.5'>
      {resolvedLabel !== '' && (
        <div className='flex items-center justify-between'>
          <LabelWithIndicator
            label={resolvedLabel}
            isRequired={!readOnly && resolvedRequired}
            isEdited={!readOnly && isEdited}
          />
          {!readOnly && labelRightElement}
        </div>
      )}
      {readOnly ? (
        readOnlyContent
      ) : (
        <PropertyInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={disabled}
          trailingAddon={trailingAddon}
        />
      )}
      {!readOnly && error && (
        <span className='text-sm text-utility-error-600'>{error}</span>
      )}
      {!readOnly && !error && hint && <HintText>{hint}</HintText>}
    </div>
  );
}
